import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronRight, Building2, Briefcase, Mail, UserPlus, MessageSquare, Clock, Sparkles, User as UserIcon, Edit2, Brain, Linkedin, Trash2, ArrowUp, ArrowDown, ArrowUpDown, QrCode, ArrowRightLeft, GitMerge, Eye } from 'lucide-react';
import ContactVCardQR from './ContactVCardQR';
import TransferContactModal from './TransferContactModal';
import MergeContactsModal from './MergeContactsModal';
import EmpresaQuickViewModal from './EmpresaQuickViewModal';
import { EditableCell } from './EditableCell';
import { formatCPF, onlyDigits } from '../utils/formatDocument';
import EmpresaInlineEditPanel from './EmpresaInlineEditPanel';
import CollapsibleSection from './CollapsibleSection';
import { SENIORIDADE_OPTIONS } from '../hooks/useLookupTables';
import OfertaInlineCascata from './imoveis/OfertaInlineCascata';
import { useOptions } from '../contexts/OptionsContext';
import { useAuth } from '../hooks/useAuth';
import { getStageColor, colaboradores } from '../utils/stageHelpers';
import { getFUPStatus, formatDate } from '../utils/dateHelpers';
import { DEAL_STAGES } from '../constants/dealStages';
import { VerticalBadge } from './launches/VerticalBadge';
import { supabase } from '../supabaseClient';

/**
 * CellHoverPopover — exibe um card flutuante grande com o conteudo completo
 * apos um dwell de 300ms sobre a celula. Permanece visivel se o mouse vai
 * para o popover. Posicionamento abaixo ou acima conforme espaco disponivel.
 */
const CellHoverPopover = ({ children, content, label, className = '', style = null }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, showAbove: false });
  const cellRef = useRef(null);
  const timeoutRef = useRef(null);

  const showPopover = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      if (cellRef.current) {
        const rect = cellRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const showAbove = spaceBelow < 250;
        setPosition({
          top: showAbove ? rect.top - 8 : rect.bottom + 8,
          left: Math.max(8, Math.min(rect.left, window.innerWidth - 620)),
          showAbove,
        });
        setIsVisible(true);
      }
    }, 300);
  }, []);

  const hidePopover = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsVisible(false), 100);
  }, []);

  const keepPopover = useCallback(() => {
    clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  if (!content) return <div className={className} style={style || undefined}>{children}</div>;

  return (
    <>
      <div
        ref={cellRef}
        onMouseEnter={showPopover}
        onMouseLeave={hidePopover}
        className={className}
        style={style || undefined}
      >
        {children}
      </div>
      {isVisible && (
        <div
          onMouseEnter={keepPopover}
          onMouseLeave={() => setIsVisible(false)}
          className="fixed z-[9999] bg-white border border-slate-200 rounded-xl shadow-2xl p-4 max-w-[600px] w-[600px]"
          style={{
            top: position.showAbove ? 'auto' : `${position.top}px`,
            bottom: position.showAbove ? `${window.innerHeight - position.top}px` : 'auto',
            left: `${position.left}px`,
          }}
        >
          <div className="text-[10px] uppercase tracking-wider font-semibold text-orange-600 mb-2 flex items-center gap-1">
            <Sparkles size={10} /> {label}
          </div>
          <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
            {content}
          </div>
        </div>
      )}
    </>
  );
};

/**
 * ResizeHandle — borda direita arrastavel que redimensiona uma coluna.
 * onStart recebe o evento; chamador captura startWidth e instala mousemove.
 */
const ResizeHandle = ({ onStart }) => (
  <div
    onMouseDown={onStart}
    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-orange-400 active:bg-orange-500 z-10"
    title="Arrastar para redimensionar"
  />
);

/**
 * SortableHeader — header clicavel com icone de ordenacao.
 */
const SortableHeader = ({ columnKey, sortConfig, onSort, children, className = '' }) => {
  const isActive = sortConfig.key === columnKey;
  const Icon = !isActive ? ArrowUpDown : (sortConfig.direction === 'asc' ? ArrowUp : ArrowDown);
  const iconColor = isActive ? 'text-orange-500' : 'text-slate-300';
  return (
    <button
      type="button"
      onClick={() => onSort(columnKey)}
      className={`flex items-center gap-0.5 hover:text-slate-700 cursor-pointer select-none ${className}`}
    >
      {children} <Icon size={10} className={`${iconColor} ml-0.5`} />
    </button>
  );
};

/**
 * ContactsExpandedTable — Fase 1 (Contatos): tabela expandida de contatos
 * de uma empresa com sortConfig próprio (Rules of Hooks: extracao evita
 * hooks dentro de loop no parent).
 *
 * Sorting null-safe (a[key] ?? '' antes de comparar). Sort é LOCAL ao bloco;
 * não interfere com ordenação de empresas.
 *
 * Os 3 grupos (active/functional/former) e botão QR vCard são adicionados
 * em commits subsequentes (Entregas 1 e 3 desta fase).
 */
const ContactColumnHeader = ({ columnKey, sortConfig, onSort, children, className = '', align = 'left' }) => {
  const isActive = sortConfig.key === columnKey;
  const Icon = !isActive ? ArrowUpDown : (sortConfig.direction === 'asc' ? ArrowUp : ArrowDown);
  const iconColor = isActive ? 'text-orange-500' : 'text-slate-300';
  const justify = align === 'right' ? 'justify-end' : 'justify-start';
  return (
    <button
      type="button"
      onClick={() => onSort(columnKey)}
      className={`flex items-center gap-0.5 ${justify} hover:text-slate-700 cursor-pointer select-none w-full ${className}`}
      title={`Ordenar por ${typeof children === 'string' ? children : columnKey}`}
    >
      <span>{children}</span> <Icon size={10} className={`${iconColor} ml-0.5`} />
    </button>
  );
};

// Formata "Mmm/AAAA" pt-BR a partir de uma date 'YYYY-MM-DD'.
const formatYearMonth = (iso) => {
  if (!iso) return null;
  const m = String(iso).match(/^(\d{4})-(\d{2})/);
  if (!m) return null;
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const idx = parseInt(m[2], 10) - 1;
  if (idx < 0 || idx > 11) return null;
  return `${monthNames[idx]}/${m[1]}`;
};

const ContactsExpandedTable = ({
  cts,
  empresa,
  employmentStatusMap,
  onUpdateContact,
  onEnrichContact,
  onSendEmail,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'nome', direction: 'asc' });
  const [qrContact, setQrContact] = useState(null);
  const [transferContact, setTransferContact] = useState(null);
  const [mergeMaster, setMergeMaster] = useState(null);

  // Vertical context dos contatos visíveis
  const contactIds = useMemo(() => (cts || []).map(c => c.id).filter(Boolean), [cts]);
  const contactIdsKey = contactIds.join(','); // estável para dep array

  const [verticalByContact, setVerticalByContact] = useState(new Map());
  useEffect(() => {
    if (!contactIds.length) { setVerticalByContact(new Map()); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('v_contact_vertical_context')
        .select('contact_id, business_unit_code, context_completeness, missing_context_fields, is_current')
        .in('contact_id', contactIds);
      if (cancelled) return;
      const map = new Map();
      (data || []).forEach((r) => {
        if (!r.contact_id) return;
        const existing = map.get(r.contact_id) || { verticals: new Set(), completeness: 'complete', missingFields: [], isHistorical: false };
        if (r.business_unit_code) existing.verticals.add(r.business_unit_code);
        if (r.context_completeness === 'partial') {
          existing.completeness = 'partial';
          existing.missingFields = [...new Set([...existing.missingFields, ...(r.missing_context_fields || [])])];
        } else if (r.context_completeness === 'no_employment' && existing.completeness !== 'partial') {
          existing.completeness = 'no_employment';
        }
        map.set(r.contact_id, existing);
      });
      setVerticalByContact(map);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactIdsKey]);

  const getVerticalProps = useCallback((contactId) => {
    const v = verticalByContact.get(contactId);
    if (!v || v.verticals.size === 0) return { vertical: null, completeness: 'no_employment', missingFields: [] };
    const verticals = Array.from(v.verticals);
    const vertical = verticals.length === 2 ? 'both' : verticals[0];
    return { vertical, completeness: v.completeness, missingFields: v.missingFields };
  }, [verticalByContact]);

  const requestSort = useCallback((key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  }, []);

  // Comparator null-safe — nulls e strings vazias caem no final independente
  // da direcao (cmp normal posiciona null no inicio em asc).
  const sortGroup = useCallback((arr) => {
    const out = [...(arr || [])];
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    out.sort((a, b) => {
      let valA;
      let valB;
      if (sortConfig.key === 'nome') {
        valA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
        valB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
      } else {
        valA = a?.[sortConfig.key] ?? '';
        valB = b?.[sortConfig.key] ?? '';
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();
      }
      const aEmpty = valA === '' || valA == null;
      const bEmpty = valB === '' || valB == null;
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      if (valA < valB) return -1 * dir;
      if (valA > valB) return 1 * dir;
      return 0;
    });
    return out;
  }, [sortConfig]);

  // Classifica contatos em 3 grupos. Sort aplicado dentro de cada grupo.
  // - functional: contact_nature='functional_address' (gating do QR + render compacto)
  // - former: tem employment is_current=false PARA ESTA empresa, sem is_current=true
  //   na mesma empresa
  // - active: todo o resto (inclui contatos legados sem employment — fallback seguro)
  const { activeByVertical, functional, former, verticalCounts } = useMemo(() => {
    const result = {
      activeByVertical: { lands: [], launches: [], both: [], unclassified: [] },
      functional: [],
      former: [],
    };

    for (const c of (cts || [])) {
      if (c.contact_nature === 'functional_address') {
        result.functional.push(c);
        continue;
      }
      const empStatus = employmentStatusMap?.get?.(c.id);
      const isEx = empStatus && empresa?.id && empStatus.isExOfCompany(empresa.id);
      if (isEx) {
        result.former.push(c);
        continue;
      }

      const v = verticalByContact.get(c.id);
      const verticals = v ? Array.from(v.verticals) : [];
      if (verticals.length === 2) result.activeByVertical.both.push(c);
      else if (verticals.includes('lands')) result.activeByVertical.lands.push(c);
      else if (verticals.includes('launches')) result.activeByVertical.launches.push(c);
      else result.activeByVertical.unclassified.push(c);
    }

    return {
      activeByVertical: {
        lands: sortGroup(result.activeByVertical.lands),
        launches: sortGroup(result.activeByVertical.launches),
        both: sortGroup(result.activeByVertical.both),
        unclassified: sortGroup(result.activeByVertical.unclassified),
      },
      functional: sortGroup(result.functional),
      former: sortGroup(result.former),
      verticalCounts: {
        lands: result.activeByVertical.lands.length,
        launches: result.activeByVertical.launches.length,
        both: result.activeByVertical.both.length,
        unclassified: result.activeByVertical.unclassified.length,
      },
    };
  }, [cts, empresa?.id, employmentStatusMap, sortGroup, verticalByContact]);

  return (
    <div className="bg-white border border-slate-200 rounded overflow-x-auto">
      {/* Header — 2/2/2/2/3/1 = 12 cols */}
      <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-semibold text-slate-500 min-w-[900px]">
        <div className="col-span-2">
          <ContactColumnHeader columnKey="nome" sortConfig={sortConfig} onSort={requestSort}>Nome</ContactColumnHeader>
        </div>
        <div className="col-span-2">
          <ContactColumnHeader columnKey="title" sortConfig={sortConfig} onSort={requestSort}>Cargo</ContactColumnHeader>
        </div>
        <div className="col-span-2">
          <ContactColumnHeader columnKey="mobile" sortConfig={sortConfig} onSort={requestSort}>Celular</ContactColumnHeader>
        </div>
        <div className="col-span-2">
          <ContactColumnHeader columnKey="phone" sortConfig={sortConfig} onSort={requestSort}>Telefone</ContactColumnHeader>
        </div>
        <div className="col-span-3">
          <ContactColumnHeader columnKey="email" sortConfig={sortConfig} onSort={requestSort}>Email</ContactColumnHeader>
        </div>
        <div className="col-span-1 text-right">Ações</div>
      </div>
      {(() => {
        const renderPersonRow = (c) => {
          const empStatus = employmentStatusMap?.get?.(c.id);
          const isEx = empStatus && empresa?.id && empStatus.isExOfCompany(empresa.id);
          const saveContact = (campo, valor) => onUpdateContact(c.id, { [campo]: valor });
          // Período do vínculo "ex" (se disponível) — pega o ex-employment mais
          // recente dessa empresa para tooltip "Mmm/AAAA — Mmm/AAAA".
          let periodLabel = null;
          if (isEx && empresa?.id && empStatus?.getEmploymentForCompany) {
            const emps = empStatus.getEmploymentForCompany(empresa.id).filter((e) => !e.is_current);
            const last = emps.find(Boolean);
            if (last && (last.start_date || last.end_date)) {
              const ini = formatYearMonth(last.start_date);
              const fim = formatYearMonth(last.end_date) || 'hoje';
              periodLabel = ini ? `${ini} — ${fim}` : fim;
            }
          }
          const exTitle = periodLabel
            ? `Ex-funcionario desta empresa (${periodLabel})`
            : empStatus?.isCurrentAnywhere
              ? 'Ex-funcionario desta empresa (atualmente em outra empresa no CRM)'
              : 'Ex-funcionario desta empresa';
          return (
            <div
              key={c.id}
              className={`grid grid-cols-12 gap-2 items-start px-2 py-1.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 text-xs min-w-[900px] ${isEx ? 'opacity-60' : ''}`}
            >
              <div className="col-span-2 min-w-0 text-slate-800 font-medium">
                <div className={`flex items-center gap-1 ${isEx ? 'line-through text-slate-500' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <EditableCell
                      value={c.first_name || ''}
                      onSave={(v) => saveContact('first_name', v || null)}
                      placeholder="Nome"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <EditableCell
                      value={c.last_name || ''}
                      onSave={(v) => saveContact('last_name', v || null)}
                      placeholder="Sobrenome"
                    />
                  </div>
                  {c.eh_decisor && (
                    <span className="text-[9px] flex-shrink-0" title="Decisor">⭐</span>
                  )}
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  {isEx && (
                    <span
                      className="text-[9px] px-1 py-0.5 bg-gray-200 text-gray-700 rounded flex-shrink-0"
                      title={exTitle}
                    >
                      Ex
                    </span>
                  )}
                  <button
                    onClick={() => saveContact('eh_decisor', !c.eh_decisor)}
                    className={`text-[9px] px-1 py-0.5 rounded flex-shrink-0 ${
                      c.eh_decisor
                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                        : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                    }`}
                    title={c.eh_decisor ? 'Marcado como decisor — clique para remover' : 'Marcar como decisor'}
                  >
                    {c.eh_decisor ? '★ decisor' : '☆ decisor'}
                  </button>
                </div>
                <div className="mt-1">
                  <VerticalBadge {...getVerticalProps(c.id)} size="sm" />
                </div>
              </div>
              <div className="col-span-2 min-w-0 text-slate-600">
                <EditableCell
                  value={c.title || ''}
                  onSave={(v) => saveContact('title', v || null)}
                  placeholder="Cargo"
                />
                <div className="grid grid-cols-2 gap-1 mt-0.5 text-[10px] text-slate-400">
                  <EditableCell
                    type="select"
                    value={c.senioridade || ''}
                    options={SENIORIDADE_OPTIONS}
                    onSave={(v) => saveContact('senioridade', v || null)}
                    placeholder="senioridade"
                  />
                  <EditableCell
                    value={c.departamento || ''}
                    onSave={(v) => saveContact('departamento', v || null)}
                    placeholder="departamento"
                  />
                </div>
              </div>
              <div className="col-span-2 text-slate-600 text-[11px] whitespace-nowrap">
                <EditableCell
                  type="tel"
                  value={c.mobile || ''}
                  onSave={(v) => saveContact('mobile', v || null)}
                  placeholder="+55 11 9..."
                  noTruncate
                />
              </div>
              <div className="col-span-2 text-slate-600 text-[11px] whitespace-nowrap">
                <EditableCell
                  type="tel"
                  value={c.phone || ''}
                  onSave={(v) => saveContact('phone', v || null)}
                  placeholder="+55 11 3..."
                  noTruncate
                />
              </div>
              <div className="col-span-3 min-w-0 text-slate-600">
                <EditableCell
                  type="email"
                  value={c.email || ''}
                  onSave={(v) => saveContact('email', v || null)}
                  placeholder="email@..."
                />
                <div className="text-[10px] text-slate-400 mt-0.5">
                  <EditableCell
                    value={c.person_linkedin_url || ''}
                    onSave={(v) => saveContact('person_linkedin_url', v || null)}
                    placeholder="linkedin.com/in/..."
                  />
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                  <EditableCell
                    value={c.cpf || ''}
                    onSave={(v) => saveContact('cpf', onlyDigits(v) || null)}
                    displayValue={c.cpf ? formatCPF(c.cpf) : undefined}
                    placeholder="CPF"
                  />
                </div>
              </div>
              <div className="col-span-1 flex items-center justify-end gap-0.5">
                {c.person_linkedin_url && (
                  <a
                    href={c.person_linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 text-slate-400 hover:text-blue-600 rounded"
                    title="Abrir LinkedIn"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Linkedin size={12} />
                  </a>
                )}
                {onEnrichContact && (
                  <button
                    onClick={() => onEnrichContact(c, empresa)}
                    className={`p-1 rounded hover:bg-amber-50 ${
                      (!c.email || (!c.phone && !c.mobile))
                        ? 'text-amber-500 hover:text-amber-700'
                        : 'text-slate-400 hover:text-amber-600'
                    }`}
                    title={c.apollo_person_id
                      ? 'Enriquecer via Apollo (preview)'
                      : 'Sem apollo_person_id — clique para tentar buscar'}
                  >
                    <Sparkles size={12} />
                  </button>
                )}
                {c.email && (
                  <button
                    onClick={() => onSendEmail(c)}
                    className="p-1 text-slate-400 hover:text-indigo-600 rounded"
                    title="Enviar email"
                  >
                    <Mail size={12} />
                  </button>
                )}
                {/* QR é suprimido em ex-funcionários (dados potencialmente desatualizados). */}
                {!isEx && (
                  <button
                    onClick={() => setQrContact(c)}
                    aria-label={`Gerar QR Code vCard para ${c.first_name || ''} ${c.last_name || ''}`.trim()}
                    className="p-1 text-slate-400 hover:text-emerald-600 rounded"
                    title="Gerar QR Code vCard"
                  >
                    <QrCode size={12} />
                  </button>
                )}
                {!isEx && (
                  <button
                    onClick={() => setTransferContact(c)}
                    aria-label={`Transferir ${c.first_name || ''} para outra empresa`.trim()}
                    className="p-1 text-slate-400 hover:text-orange-600 rounded"
                    title="Transferir para outra empresa"
                  >
                    <ArrowRightLeft size={12} />
                  </button>
                )}
                <button
                  onClick={() => setMergeMaster(c)}
                  aria-label={`Mesclar contatos a partir de ${c.first_name || ''}`.trim()}
                  className="p-1 text-slate-400 hover:text-violet-600 rounded"
                  title="Mesclar com outro contato (este é o master)"
                >
                  <GitMerge size={12} />
                </button>
              </div>
            </div>
          );
        };

        const renderFunctionalRow = (c) => {
          const saveContact = (campo, valor) => onUpdateContact(c.id, { [campo]: valor });
          return (
            <div
              key={c.id}
              className="grid grid-cols-12 gap-2 items-center px-2 py-1.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 text-xs min-w-[900px]"
            >
              <div className="col-span-2 min-w-0 text-slate-700 flex items-center gap-1.5">
                <span className="text-base leading-none flex-shrink-0" title="Endereço funcional">📧</span>
                <span className="text-[11px] truncate" title={`${c.first_name || ''} ${c.last_name || ''}`.trim() || '—'}>
                  {`${c.first_name || ''} ${c.last_name || ''}`.trim() || '—'}
                </span>
              </div>
              <div className="col-span-2 text-[10px] text-slate-400 italic">funcional</div>
              <div className="col-span-2 text-[10px] text-slate-300">—</div>
              <div className="col-span-2 text-[10px] text-slate-300">—</div>
              <div className="col-span-3 min-w-0 text-slate-600">
                <EditableCell
                  type="email"
                  value={c.email || ''}
                  onSave={(v) => saveContact('email', v || null)}
                  placeholder="email@..."
                />
              </div>
              <div className="col-span-1 flex items-center justify-end gap-0.5">
                {c.email && (
                  <button
                    onClick={() => onSendEmail(c)}
                    className="p-1 text-slate-400 hover:text-indigo-600 rounded"
                    title="Enviar email"
                  >
                    <Mail size={12} />
                  </button>
                )}
              </div>
            </div>
          );
        };

        const groupHeader = (label) => (
          <div className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold text-slate-400 bg-slate-50/70 border-b border-slate-100">
            {label}
          </div>
        );

        return (
          <>
            {/* Resumo por vertical no topo */}
            {(verticalCounts.lands + verticalCounts.launches + verticalCounts.both + verticalCounts.unclassified) > 0 && (
              <div className="px-2 py-1.5 bg-slate-50/50 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-3 flex-wrap">
                <span>Por vertical:</span>
                {verticalCounts.lands > 0 && (
                  <span className="flex items-center gap-1 normal-case tracking-normal">
                    <VerticalBadge vertical="lands" size="sm" /> <span>{verticalCounts.lands}</span>
                  </span>
                )}
                {verticalCounts.launches > 0 && (
                  <span className="flex items-center gap-1 normal-case tracking-normal">
                    <VerticalBadge vertical="launches" size="sm" /> <span>{verticalCounts.launches}</span>
                  </span>
                )}
                {verticalCounts.both > 0 && (
                  <span className="flex items-center gap-1 normal-case tracking-normal">
                    <VerticalBadge vertical="both" size="sm" /> <span>{verticalCounts.both}</span>
                  </span>
                )}
                {verticalCounts.unclassified > 0 && (
                  <span className="flex items-center gap-1 normal-case tracking-normal">
                    <VerticalBadge vertical={null} size="sm" /> <span>{verticalCounts.unclassified}</span>
                  </span>
                )}
              </div>
            )}

            {/* Grupos por vertical (dentro de active) */}
            {activeByVertical.lands.length > 0 && groupHeader(`Lands (${activeByVertical.lands.length})`)}
            {activeByVertical.lands.map(renderPersonRow)}

            {activeByVertical.launches.length > 0 && groupHeader(`Launches (${activeByVertical.launches.length})`)}
            {activeByVertical.launches.map(renderPersonRow)}

            {activeByVertical.both.length > 0 && groupHeader(`Ambas verticais (${activeByVertical.both.length})`)}
            {activeByVertical.both.map(renderPersonRow)}

            {activeByVertical.unclassified.length > 0 && groupHeader(`Sem classificação (${activeByVertical.unclassified.length})`)}
            {activeByVertical.unclassified.map(renderPersonRow)}

            {/* Grupos legados preservados */}
            {functional.length > 0 && groupHeader(`Endereços funcionais (${functional.length})`)}
            {functional.map(renderFunctionalRow)}

            {former.length > 0 && groupHeader(`Ex-funcionários (${former.length})`)}
            {former.map(renderPersonRow)}
          </>
        );
      })()}
      {qrContact && (
        <ContactVCardQR
          contact={qrContact}
          company={empresa}
          onClose={() => setQrContact(null)}
        />
      )}
      {transferContact && (
        <TransferContactModal
          contact={transferContact}
          currentEmpresa={empresa}
          onClose={() => setTransferContact(null)}
          onTransferred={() => setTransferContact(null)}
        />
      )}
      {mergeMaster && (
        <MergeContactsModal
          masterContact={{ ...mergeMaster, empresas: empresa }}
          duplicateContact={null}
          onClose={() => setMergeMaster(null)}
          onMerged={() => setMergeMaster(null)}
        />
      )}
    </div>
  );
};

/**
 * OfertasTable — encapsula header + map de OfertaRow para uma empresa,
 * com escopo proprio para sortConfig (Rules of Hooks: extracao evita
 * hooks dentro de loop no parent).
 */
const OfertasTable = ({
  ofertas,
  dealStages,
  briefingByOferta,
  ppCountByOferta,
  highlightedStageSet,
  cts,
  onOfertaClick,
  handleUpdateOfertaField,
  onSendEmail,
  handleDiscardOferta,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'nome', direction: 'asc' });
  // null = usar largura padrao (flex ou w-fixed); numero = px overridden via drag
  const [colWidths, setColWidths] = useState({ oferta: null, resumo: null, proximo: null });
  const headerRefs = useRef({});

  const startResize = useCallback((key) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = headerRefs.current[key]?.getBoundingClientRect().width || 200;
    const onMouseMove = (ev) => {
      const w = Math.max(80, startWidth + (ev.clientX - startX));
      setColWidths((prev) => ({ ...prev, [key]: w }));
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const requestSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const respOptionsLocal = useMemo(
    () => colaboradores.filter((c) => c.id !== 'all').map((c) => ({ value: c.id, label: c.nome })),
    []
  );

  const getResponsavelNome = useCallback((id) => {
    if (!id) return '';
    return respOptionsLocal.find((r) => r.value === id)?.label || '';
  }, [respOptionsLocal]);

  const sortedOfertas = useMemo(() => {
    const arr = [...ofertas];
    arr.sort((a, b) => {
      let valA;
      let valB;
      switch (sortConfig.key) {
        case 'nome':
          valA = (a?.nome || '').toLowerCase();
          valB = (b?.nome || '').toLowerCase();
          break;
        case 'resumo_estagio':
          valA = (a?.resumo_estagio || '').toLowerCase();
          valB = (b?.resumo_estagio || '').toLowerCase();
          break;
        case 'proximo_passo': {
          const ppA = briefingByOferta?.get?.(a.id)?.proximo_passo;
          const ppB = briefingByOferta?.get?.(b.id)?.proximo_passo;
          valA = (typeof ppA === 'object' ? ppA?.acao : ppA || '').toLowerCase();
          valB = (typeof ppB === 'object' ? ppB?.acao : ppB || '').toLowerCase();
          break;
        }
        case 'data_fup':
          valA = a?.data_fup || '';
          valB = b?.data_fup || '';
          break;
        case 'responsavel':
          valA = (a?.colaboradores?.nome || getResponsavelNome(a?.responsavel_id) || '').toLowerCase();
          valB = (b?.colaboradores?.nome || getResponsavelNome(b?.responsavel_id) || '').toLowerCase();
          break;
        default:
          valA = '';
          valB = '';
      }
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [ofertas, sortConfig, briefingByOferta, getResponsavelNome]);

  // Estilos dinamicos: se colWidths[key] eh number => px fixo; senao default
  const ofertaStyle = colWidths.oferta != null
    ? { width: colWidths.oferta, minWidth: colWidths.oferta, flexShrink: 0 }
    : null;
  const resumoStyle = colWidths.resumo != null
    ? { width: colWidths.resumo, minWidth: colWidths.resumo, flex: '0 0 auto' }
    : null;
  const proximoStyle = colWidths.proximo != null
    ? { width: colWidths.proximo, minWidth: colWidths.proximo, flex: '0 0 auto' }
    : null;
  const ofertaClass = colWidths.oferta != null ? '' : 'w-[216px] min-w-[216px] flex-shrink-0';
  const resumoClass = colWidths.resumo != null ? 'min-w-0' : 'flex-[35] min-w-0';
  const proximoClass = colWidths.proximo != null ? 'min-w-0' : 'flex-[30] min-w-0';

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2 px-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">
        <div
          ref={(el) => { headerRefs.current.oferta = el; }}
          className={`relative ${ofertaClass}`}
          style={ofertaStyle || undefined}
        >
          <SortableHeader columnKey="nome" sortConfig={sortConfig} onSort={requestSort}>
            Oferta / Estágio
          </SortableHeader>
          <ResizeHandle onStart={startResize('oferta')} />
        </div>
        <div
          ref={(el) => { headerRefs.current.resumo = el; }}
          className={`relative ${resumoClass}`}
          style={resumoStyle || undefined}
        >
          <SortableHeader columnKey="resumo_estagio" sortConfig={sortConfig} onSort={requestSort}>
            Resumo
          </SortableHeader>
          <ResizeHandle onStart={startResize('resumo')} />
        </div>
        <div
          ref={(el) => { headerRefs.current.proximo = el; }}
          className={`relative ${proximoClass}`}
          style={proximoStyle || undefined}
        >
          <SortableHeader columnKey="proximo_passo" sortConfig={sortConfig} onSort={requestSort}>
            Próximo passo
          </SortableHeader>
          <ResizeHandle onStart={startResize('proximo')} />
        </div>
        <div className="w-[70px] flex-shrink-0">
          <SortableHeader columnKey="data_fup" sortConfig={sortConfig} onSort={requestSort}>
            Data FUP
          </SortableHeader>
        </div>
        <div className="w-[70px] flex-shrink-0">
          <SortableHeader columnKey="responsavel" sortConfig={sortConfig} onSort={requestSort}>
            Resp.
          </SortableHeader>
        </div>
        <div className="w-[50px] flex-shrink-0 text-right">Ações</div>
      </div>

      <div className="group/rows space-y-2">
      {sortedOfertas.map((oferta) => {
        const dim = highlightedStageSet.size > 0 && !highlightedStageSet.has(oferta?.status);
        const briefingSummary = briefingByOferta?.get?.(oferta.id) || null;
        const ppCount = ppCountByOferta?.get?.(oferta.id) || null;
        return (
          <OfertaRow
            key={oferta.id}
            oferta={oferta}
            dealStages={dealStages}
            briefingSummary={briefingSummary}
            ppCount={ppCount}
            onClickOferta={() => onOfertaClick(oferta.id)}
            onUpdateField={(campo, valor) => handleUpdateOfertaField(oferta, campo, valor)}
            onSendEmail={() => {
              const contato = cts[0] || null;
              if (contato) onSendEmail(contato);
            }}
            onDiscard={() => handleDiscardOferta(oferta)}
            hasContacts={cts.length > 0}
            dimmed={dim}
            colWidths={colWidths}
          />
        );
      })}
      </div>
    </div>
  );
};

/**
 * Tabela principal — "uma linha por empresa + expansão com linhas de oferta".
 * v3 (Sessão 3): adiciona botão Contexto IA na linha da empresa.
 *
 * Defensive coding: todo consumo de helper/dado externo tem fallback explícito.
 *
 * ATENÇÃO: `updateOferta`, `buildHITLPatchManual`, `buildHITLPatchDataFup` vêm do
 * Dashboard (mesma instância de `useOfertas`). NÃO instanciar um 2º `useOfertas`
 * aqui — isso criava um state isolado que fazia a UI não refletir updates
 * imediatamente (fix v4: otimistic update só chega se tudo usa a mesma instância).
 */
export const ContactTable = ({
  contatos,
  ofertasPorEmpresa,
  onOfertaClick,
  onEditFUP,
  onSendEmail,
  onUpdateContact,
  onUpdateEmpresa,
  onAddContact,
  onOpenHistory,
  onOpenAIContext,
  updateOferta,
  buildHITLPatchManual,
  buildHITLPatchDataFup,
  highlightedStages,
  briefingByOferta,
  ppCountByOferta,
  onEnrichContact,
  employmentStatusMap,
  focusEmpresaId,
  selectedAreaIds,
  areaFilterMode,
}) => {
  const highlightedStageSet = React.useMemo(
    () => new Set((highlightedStages || []).filter(Boolean)),
    [highlightedStages]
  );
  const areaFilterIdSet = React.useMemo(
    () => new Set((selectedAreaIds || []).filter(Boolean)),
    [selectedAreaIds]
  );
  const hasAreaFilter = areaFilterIdSet.size > 0 && areaFilterMode === 'com';
  const { dealStages } = useOptions();
  const { user } = useAuth();

  const [expandedCompanies, setExpandedCompanies] = useState(new Set());
  const [quickViewEmpresaId, setQuickViewEmpresaId] = useState(null);

  // Onda 4 abr/26: foco externo (navegação de /proprietarios → Dashboard expandido).
  React.useEffect(() => {
    if (!focusEmpresaId) return;
    setExpandedCompanies((prev) => {
      if (prev.has(focusEmpresaId)) return prev;
      const next = new Set(prev);
      next.add(focusEmpresaId);
      return next;
    });
    const t = setTimeout(() => {
      const el = document.getElementById(`empresa-row-${focusEmpresaId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 400);
    return () => clearTimeout(t);
  }, [focusEmpresaId]);

  const empresasMap = new Map();
  (contatos || []).forEach(c => {
    const empId = c.empresas?.id;
    if (!empId) return;
    if (!empresasMap.has(empId)) {
      empresasMap.set(empId, { empresa: c.empresas, contatos: [] });
    }
    if (!c._isVirtual) {
      empresasMap.get(empId).contatos.push(c);
    }
  });

  const empresas = Array.from(empresasMap.values()).sort((a, b) =>
    (a.empresa.empresa || '').localeCompare(b.empresa.empresa || '')
  );

  const toggleExpand = useCallback((empresaId) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(empresaId)) next.delete(empresaId);
      else next.add(empresaId);
      return next;
    });
  }, []);

  const handleDiscardOferta = async (oferta) => {
    const nome = oferta?.nome || 'esta oferta';
    // eslint-disable-next-line no-alert
    const reason = window.prompt(`Motivo para descartar "${nome}" (opcional):`);
    if (reason === null) return;
    const who = user?.email || user?.id || null;
    try {
      await updateOferta(oferta.id, {
        status: DEAL_STAGES.DESCARTADA,
        discarded_at: new Date().toISOString(),
        discarded_by: who,
        discard_reason: reason || null,
      });
    } catch (err) {
      console.error('Erro ao descartar oferta:', err);
      // eslint-disable-next-line no-alert
      alert(`Erro ao descartar: ${err.message}`);
    }
  };

  const handleUpdateOfertaField = async (oferta, campo, novoValor) => {
    try {
      let patch;
      if (campo === 'data_fup') {
        patch = buildHITLPatchDataFup(novoValor, user?.email);
      } else if (campo === 'status') {
        patch = { status: novoValor, updated_at: new Date().toISOString() };
      } else if (campo === 'responsavel_id') {
        patch = { responsavel_id: novoValor, updated_at: new Date().toISOString() };
      } else if (campo === 'fup_contato_id') {
        patch = { fup_contato_id: novoValor, updated_at: new Date().toISOString() };
      } else {
        patch = buildHITLPatchManual(campo, novoValor, oferta, user?.email);
      }
      await updateOferta(oferta.id, patch);
    } catch (err) {
      console.error(`Erro ao atualizar ${campo}:`, err);
      alert(`Erro ao atualizar: ${err.message}`);
    }
  };

  return (
    <>
    <div className="bg-white rounded-lg shadow-sm border">
      <div>
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
          <tr>
            <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wider w-10 overflow-hidden resize-x" style={{ minWidth: '40px' }}></th>
            <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wider overflow-hidden resize-x" style={{ minWidth: '200px' }}>Empresa ▲</th>
            <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wider w-32 overflow-hidden resize-x" style={{ minWidth: '100px' }}>Ofertas</th>
            <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wider w-32 overflow-hidden resize-x" style={{ minWidth: '100px' }}>Contatos</th>
            <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wider w-24 overflow-hidden resize-x" style={{ minWidth: '80px' }}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {empresas.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center py-10 text-slate-500 text-sm">
                Nenhuma empresa para os filtros atuais.
              </td>
            </tr>
          )}
          {empresas.map(({ empresa, contatos: cts }) => {
            const isExpanded = expandedCompanies.has(empresa.id);
            const isFocused = focusEmpresaId === empresa.id;
            const ofertasAll = ofertasPorEmpresa.get(empresa.id) || [];
            const ofertas = hasAreaFilter
              ? ofertasAll.filter((o) => o.area_id && areaFilterIdSet.has(o.area_id))
              : ofertasAll;
            return (
              <React.Fragment key={empresa.id}>
                <tr
                  id={`empresa-row-${empresa.id}`}
                  onClick={() => toggleExpand(empresa.id)}
                  className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer scroll-mt-[240px] ${isFocused ? 'ring-2 ring-blue-400 bg-blue-50/40' : ''}`}
                >
                  <td className="py-2 px-3 text-slate-400">
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      <Building2 size={14} className="text-blue-600 flex-shrink-0" />
                      <span className="font-medium text-sm text-slate-800">{empresa.empresa}</span>
                      {empresa.apollo_id && (
                        <span
                          title="Enriquecida via Apollo"
                          className="text-xs leading-none"
                        >
                          🟢
                        </span>
                      )}
                      {empresa.status_operacional === 'liquidated' && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-semibold">⚠️ Liquidada</span>
                      )}
                      {empresa.status_operacional === 'archived' && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded">📦 Arquivada</span>
                      )}
                      {empresa.status_operacional === 'inactive' && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">💤 Inativa</span>
                      )}
                      {empresa.status_operacional === 'acquired' && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded">🔀 Adquirida</span>
                      )}
                      {empresa.papel_negocio === 'service_provider' && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">⚖️ Operacional</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    {ofertas.length > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-orange-50 text-orange-700 text-xs font-medium">
                        <Briefcase size={10} /> {ofertas.length} oferta{ofertas.length > 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400 italic">sem oferta</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <span className="text-xs text-slate-600">
                      {cts.length} contato{cts.length !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); setQuickViewEmpresaId(empresa.id); }}
                        className="px-2 py-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded inline-flex items-center gap-1 transition-colors font-medium text-xs border border-blue-200 hover:border-blue-300"
                        title="Ver detalhes da empresa (QuickView)"
                        aria-label="Ver detalhes da empresa"
                      >
                        <Eye size={18} />
                        <span className="hidden sm:inline">Detalhes</span>
                      </button>
                      {onOpenAIContext && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onOpenAIContext(empresa); }}
                          className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                          title="Contexto IA desta empresa"
                        >
                          <Brain size={14} />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); onOpenHistory(empresa); }}
                        className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Histórico da empresa"
                      >
                        <Clock size={14} />
                      </button>
                    </div>
                  </td>
                </tr>

                {isExpanded && (
                  <tr>
                    <td colSpan={5} className="bg-slate-50/50 px-3 py-3">
                      <div className="mb-4">
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1.5">
                          <Briefcase size={11} className="text-orange-600" />
                          Ofertas desta empresa ({ofertas.length})
                        </div>

                        {ofertas.length === 0 && (
                          <div className="text-xs text-slate-500 italic bg-white border border-dashed border-slate-200 rounded p-3">
                            Sem ofertas cadastradas. Use o botão "Nova Oferta" no header para criar.
                          </div>
                        )}

                        {ofertas.length > 0 && (
                          <OfertasTable
                            ofertas={ofertas}
                            dealStages={dealStages}
                            briefingByOferta={briefingByOferta}
                            ppCountByOferta={ppCountByOferta}
                            highlightedStageSet={highlightedStageSet}
                            cts={cts}
                            onOfertaClick={onOfertaClick}
                            handleUpdateOfertaField={handleUpdateOfertaField}
                            onSendEmail={onSendEmail}
                            handleDiscardOferta={handleDiscardOferta}
                          />
                        )}
                      </div>

                      <div className="mb-4">
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center justify-between">
                          <span className="flex items-center gap-1.5">
                            <UserIcon size={11} className="text-blue-600" />
                            Contatos desta empresa ({cts.length})
                          </span>
                          <button
                            onClick={() => onAddContact(empresa)}
                            className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <UserPlus size={11} /> Adicionar contato
                          </button>
                        </div>

                        {cts.length === 0 ? (
                          <div className="text-xs text-slate-500 italic bg-white border border-dashed border-slate-200 rounded p-3">
                            Nenhum contato cadastrado.
                          </div>
                        ) : (
                          <ContactsExpandedTable
                            cts={cts}
                            empresa={empresa}
                            employmentStatusMap={employmentStatusMap}
                            onUpdateContact={onUpdateContact}
                            onEnrichContact={onEnrichContact}
                            onSendEmail={onSendEmail}
                          />
                        )}
                      </div>

                      <CollapsibleSection
                        title="EMPRESA — DADOS EDITÁVEIS"
                        defaultExpanded={false}
                        icon="🏢"
                      >
                        <EmpresaInlineEditPanel empresa={empresa} onUpdate={onUpdateEmpresa} />
                        {empresa.apollo_id && (
                        <div className="mt-3 px-3 py-2 bg-emerald-50/70 border border-emerald-100 rounded flex items-center gap-2 text-xs flex-wrap">
                          <span className="text-emerald-700 font-semibold flex-shrink-0">🟢 Apollo</span>
                          {empresa.apollo_industry && (
                            <>
                              <span className="text-slate-400">·</span>
                              <span className="text-slate-700 capitalize">{empresa.apollo_industry}</span>
                            </>
                          )}
                          {empresa.apollo_num_employees != null && Number(empresa.apollo_num_employees) > 0 && (
                            <>
                              <span className="text-slate-400">·</span>
                              <span className="text-slate-700">
                                {Number(empresa.apollo_num_employees).toLocaleString('pt-BR')} funcionários
                              </span>
                            </>
                          )}
                          {empresa.apollo_founded_year && (
                            <>
                              <span className="text-slate-400">·</span>
                              <span className="text-slate-700">fundada em {empresa.apollo_founded_year}</span>
                            </>
                          )}
                          {(empresa.apollo_hq_city || empresa.apollo_hq_country) && (
                            <>
                              <span className="text-slate-400">·</span>
                              <span className="text-slate-700">
                                {[empresa.apollo_hq_city, empresa.apollo_hq_country].filter(Boolean).join(', ')}
                              </span>
                            </>
                          )}
                          {empresa.apollo_keywords?.length > 0 && (
                            <>
                              <span className="text-slate-400">·</span>
                              <span
                                className="text-slate-700 truncate max-w-md"
                                title={empresa.apollo_keywords.join(', ')}
                              >
                                {empresa.apollo_keywords.slice(0, 4).join(', ')}
                                {empresa.apollo_keywords.length > 4 && (
                                  <span className="text-slate-400"> +{empresa.apollo_keywords.length - 4}</span>
                                )}
                              </span>
                            </>
                          )}
                          <a
                            href={`https://app.apollo.io/#/accounts/${empresa.apollo_id}/overview`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-auto text-emerald-700 hover:text-emerald-900 hover:underline flex-shrink-0"
                            onClick={(e) => e.stopPropagation()}
                            title="Abrir no Apollo"
                          >
                            Apollo ↗
                          </a>
                        </div>
                        )}
                      </CollapsibleSection>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
    {quickViewEmpresaId && (
      <EmpresaQuickViewModal
        empresaId={quickViewEmpresaId}
        onClose={() => setQuickViewEmpresaId(null)}
      />
    )}
    </>
  );
};

/**
 * Linha de oferta específica — defensive coding em todo acesso (Regras CLAUDE.md).
 */
/**
 * parsePrazoToDate (Fix 4 dashboard_ux_fixes_2 2026-04-26): best-effort.
 * Extrai ISO date 'YYYY-MM-DD' de texto livre como:
 *   "até 25/04/2026"  → '2026-04-25'
 *   "25/04/2026"       → '2026-04-25'
 *   "2026-04-25"       → '2026-04-25'
 *   "Urgente — até 2 dias úteis"  → null (sem data explicita)
 *
 * Regras:
 * - Se houver multiplos matches, pega o PRIMEIRO encontrado.
 * - DD/MM/YYYY e DD/MM/YY (YY → 20YY se < 50, senao 19YY).
 * - Valida que a data e valida (ex: 32/13 rejeitado).
 * - Retorna null quando nao conseguir parsear (nao mostrar botao).
 */
const parsePrazoToDate = (texto) => {
  if (!texto || typeof texto !== 'string') return null;

  const isoMatch = texto.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [_, y, m, d] = isoMatch;
    const dt = new Date(`${y}-${m}-${d}`);
    if (!isNaN(dt.getTime())) return `${y}-${m}-${d}`;
  }

  const brMatch = texto.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (brMatch) {
    let [_, d, m, y] = brMatch;
    if (y.length === 2) {
      y = (parseInt(y, 10) < 50 ? '20' : '19') + y;
    }
    const dd = d.padStart(2, '0');
    const mm = m.padStart(2, '0');
    const dt = new Date(`${y}-${mm}-${dd}T00:00:00`);
    if (!isNaN(dt.getTime())
      && dt.getMonth() + 1 === parseInt(mm, 10)
      && dt.getDate() === parseInt(dd, 10)) {
      return `${y}-${mm}-${dd}`;
    }
  }

  return null;
};

/**
 * FUP coloring (Fix 2 dashboard_ux 2026-04-26):
 * - atrasado (< hoje)  → vermelho bold
 * - hoje (= hoje)      → âmbar bold
 * - proximos 3 dias    → esmeralda
 * - futuro             → slate
 */
const getFupColor = (dataFup) => {
  if (!dataFup) return 'text-slate-400';
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const fup = new Date(dataFup);
  fup.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((fup - hoje) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'text-red-600 font-semibold';
  if (diffDays === 0) return 'text-amber-600 font-semibold';
  if (diffDays <= 3) return 'text-emerald-600';
  return 'text-slate-600';
};

const OfertaRow = ({ oferta, dealStages, briefingSummary, ppCount = null, onClickOferta, onUpdateField, onSendEmail, onDiscard, hasContacts, dimmed = false, colWidths = { oferta: null, resumo: null, proximo: null } }) => {
  const stageOptions = (dealStages || []).map(s => ({ value: s.value, label: s.label }));
  const respOptions = colaboradores.filter(c => c.id !== 'all').map(c => ({ value: c.id, label: c.nome }));

  const resumoFoiIA = oferta?.resumo_estagio_fonte && oferta.resumo_estagio_fonte !== 'manual';
  const resumoFoiEditado = oferta?.resumo_estagio_fonte === 'ai_editado';

  // Frente A resumo_enrich 2026-04-26: edicao inline expandida (full-width).
  const [editingResumo, setEditingResumo] = useState(false);
  const [editResumoText, setEditResumoText] = useState('');
  const [savingResumo, setSavingResumo] = useState(false);
  // Frente B cascata_por_oferta 2026-04-26: cascata area→imoveis→propostas inline.
  const [cascataExpanded, setCascataExpanded] = useState(false);
  const hasArea = !!oferta?.area_id;

  const handleStartEditResumo = (e) => {
    e?.stopPropagation?.();
    setEditResumoText(oferta?.resumo_estagio || '');
    setEditingResumo(true);
  };
  const handleCancelEditResumo = () => {
    setEditingResumo(false);
    setEditResumoText('');
  };
  const handleSaveResumo = async () => {
    setSavingResumo(true);
    try {
      await onUpdateField('resumo_estagio', editResumoText.trim());
      setEditingResumo(false);
      setEditResumoText('');
    } finally {
      setSavingResumo(false);
    }
  };

  const fupColorClass = getFupColor(oferta?.data_fup);

  const responsavelLabel =
    oferta?.colaboradores?.nome ||
    respOptions.find(r => r.value === oferta?.responsavel_id)?.label ||
    '—';

  const stageColorClass = getStageColor(oferta?.status) || 'bg-slate-100 text-slate-700';

  // ADR-015 Fase 2.1: fonte unica = proximo_passo_items via ppCount.latest.
  // briefingSummary.proximo_passo (JSON antigo) NAO eh mais usado para exibir.
  const ppLatest = ppCount?.latest || null;
  const ppAcao = ppLatest?.acao || null;
  const ppPrazo = ppLatest?.prazo_sugerido || null;
  const ppAutorTipo = ppLatest?.autor_tipo || null;
  const ppExtras = (ppCount?.total || 0) > 1 ? (ppCount.total - 1) : 0;

  const getUrgencyColor = (dataFup) => {
    if (!dataFup) return '';
    const days = Math.floor((new Date(dataFup) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'border-l-4 border-l-red-500 bg-red-50/50';
    if (days <= 3) return 'border-l-4 border-l-amber-500 bg-amber-50/50';
    if (days <= 7) return 'border-l-4 border-l-yellow-400 bg-yellow-50/30';
    return 'border-l-4 border-l-green-400 bg-green-50/30';
  };
  const urgencyClass = getUrgencyColor(oferta?.data_fup);

  const responsavelFirst = (responsavelLabel || '').split(' ')[0] || '—';

  const dataFupShort = oferta?.data_fup ? formatDate(oferta.data_fup).slice(0, 5) : '—';

  // Larguras dinamicas sincronizadas com o header do OfertasTable
  const ofertaColStyle = colWidths.oferta != null
    ? { width: colWidths.oferta, minWidth: colWidths.oferta, flexShrink: 0 }
    : null;
  const ofertaColClass = colWidths.oferta != null ? '' : 'w-[216px] min-w-[216px] flex-shrink-0';
  const resumoColStyle = colWidths.resumo != null
    ? { width: colWidths.resumo, minWidth: colWidths.resumo, flex: '0 0 auto' }
    : null;
  const resumoColClass = colWidths.resumo != null ? 'min-w-0' : 'flex-[35] min-w-0';
  const proximoColStyle = colWidths.proximo != null
    ? { width: colWidths.proximo, minWidth: colWidths.proximo, flex: '0 0 auto' }
    : null;
  const proximoColClass = colWidths.proximo != null
    ? `min-w-0 text-sm rounded px-2 py-1 ${urgencyClass}`
    : `flex-[30] min-w-0 text-sm rounded px-2 py-1 ${urgencyClass}`;

  return (
    <div>
    <div
      className={`relative flex items-start gap-2 bg-white border rounded p-2 transition-all duration-150 ${
        dimmed
          ? 'border-slate-200 opacity-40 hover:opacity-70'
          : 'border-slate-200 hover:border-orange-300 hover:shadow-md hover:z-10 group-hover/rows:opacity-60 hover:!opacity-100'
      }`}
      title={dimmed ? 'Oferta fora do filtro de estagio ativo' : undefined}
    >
      <div className={ofertaColClass} style={ofertaColStyle || undefined}>
        <div className="flex items-center gap-1">
          {hasArea && (
            <button
              onClick={(e) => { e.stopPropagation(); setCascataExpanded(v => !v); }}
              className="p-0.5 text-slate-400 hover:text-indigo-600 rounded flex-shrink-0"
              title={cascataExpanded ? 'Recolher cascata de imóveis' : 'Expandir cascata de imóveis da área'}
            >
              {cascataExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          )}
          <button
            onClick={onClickOferta}
            className="text-left text-xs font-medium text-slate-800 truncate hover:text-orange-600 block flex-grow min-w-0"
            title={oferta?.nome || ''}
          >
            {oferta?.nome?.includes(': ')
              ? oferta.nome.split(': ').slice(1).join(': ')
              : (oferta?.nome || '—')}
          </button>
        </div>
        <div className="mt-1">
          <EditableCell
            value={oferta?.status || ''}
            options={stageOptions}
            onSave={(v) => onUpdateField('status', v)}
            type="select"
            colorClass={stageColorClass}
            className="text-[10px] px-1.5 py-0.5 rounded"
          />
        </div>
      </div>

      {/* Resumo — texto completo (whitespace-pre-wrap); click abre editor full-width */}
      <CellHoverPopover
        content={!editingResumo && oferta?.resumo_estagio && oferta.resumo_estagio.length > 60 ? oferta.resumo_estagio : null}
        label={resumoFoiEditado ? 'Resumo do estágio · editado por humano' : 'Resumo do estágio'}
        className={resumoColClass}
        style={resumoColStyle}
      >
        <div className="relative">
          <button
            onClick={handleStartEditResumo}
            className="text-left text-sm text-slate-700 w-full leading-relaxed line-clamp-3 hover:bg-slate-50 rounded px-0.5 cursor-text min-h-[28px]"
            title="Clique para editar em tela expandida"
          >
            {oferta?.resumo_estagio || (
              <span className="text-slate-400 italic text-xs">
                {resumoFoiIA ? '(IA, clique)' : 'clique para editar'}
              </span>
            )}
          </button>
          {resumoFoiIA && (
            <div className="absolute -top-1 -right-1 flex items-center gap-0.5 pointer-events-none">
              <Sparkles size={10} className={resumoFoiEditado ? 'text-amber-500' : 'text-purple-500'} />
              {resumoFoiEditado && <Edit2 size={8} className="text-amber-500" />}
            </div>
          )}
        </div>
      </CellHoverPopover>

      {/* Próximo passo (do briefing mais recente) — borda colorida por urgência do FUP */}
      <CellHoverPopover
        content={ppAcao && ppAcao.length > 60 ? `${ppAcao}${ppPrazo ? `\n\n⏱️ Prazo: ${ppPrazo}` : ''}` : null}
        label="Próximo passo"
        className={proximoColClass}
        style={proximoColStyle}
      >
        {ppAcao ? (
          <button
            type="button"
            onClick={onClickOferta}
            className="text-left w-full hover:bg-white/60 rounded cursor-pointer"
            title="Clique para abrir os detalhes da oferta"
          >
            <div className="text-slate-700 leading-relaxed line-clamp-3">
              <span className="mr-1">{ppAutorTipo === 'agent' ? '🤖' : '📝'}</span>
              {ppAcao}
            </div>
            {ppPrazo && (
              <div className="text-[10px] text-orange-600 mt-0.5">
                ⏱️ {ppPrazo}
              </div>
            )}
            {ppExtras > 0 && (
              <div className="text-[10px] text-violet-700 font-medium mt-1">
                📋 +{ppExtras} {ppExtras === 1 ? 'ação pendente' : 'ações pendentes'}
              </div>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={onClickOferta}
            className="text-left w-full hover:bg-white/60 rounded cursor-pointer"
            title="Clique para abrir a oferta"
          >
            <span className="text-slate-400 italic text-[11px] hover:text-slate-600">sem ações pendentes</span>
          </button>
        )}
      </CellHoverPopover>

      {/* Data FUP compacta DD/MM (vermelha se atrasada) */}
      <div className="w-[70px] flex-shrink-0">
        <div className={`${fupColorClass} text-[11px]`} title={oferta?.data_fup ? `FUP: ${oferta.data_fup}` : ''}>
          <EditableCell
            value={oferta?.data_fup || ''}
            onSave={(v) => onUpdateField('data_fup', v || null)}
            type="date"
            className="text-[11px]"
            displayValue={dataFupShort}
          />
        </div>
        {(() => {
          if (!ppPrazo) return null;
          const parsed = parsePrazoToDate(ppPrazo);
          // FUP vazio + prazo parseavel → botao "aplicar IA"
          if (!oferta?.data_fup && parsed) {
            return (
              <button
                onClick={() => onUpdateField('data_fup', parsed)}
                className="text-[9px] text-blue-600 hover:text-blue-800 mt-0.5 flex items-center gap-0.5"
                title={`IA sugere: ${ppPrazo} → ${parsed}`}
              >
                ⬆ aplicar IA ({formatDate(parsed)})
              </button>
            );
          }
          // FUP preenchido mas diferente do prazo IA → aviso (so quando parseavel e realmente divergente)
          if (oferta?.data_fup && parsed && parsed !== oferta.data_fup) {
            return (
              <div
                className="text-[9px] text-blue-600 mt-0.5 truncate"
                title={`IA sugere prazo diferente: "${ppPrazo}" (${parsed})`}
              >
                IA: {formatDate(parsed)}
              </div>
            );
          }
          return null;
        })()}
      </div>

      <div className="w-[70px] flex-shrink-0" title={responsavelLabel}>
        <EditableCell
          value={oferta?.responsavel_id || ''}
          options={[{ value: '', label: '—' }, ...respOptions]}
          onSave={(v) => onUpdateField('responsavel_id', v || null)}
          type="select"
          className="text-xs"
          displayValue={responsavelFirst}
        />
      </div>

      <div className="w-[50px] flex-shrink-0 flex items-center justify-end gap-0.5">
        <button
          onClick={onClickOferta}
          className="p-0.5 text-slate-500 hover:text-orange-600 rounded"
          title="Ver detalhes da oferta"
        >
          <MessageSquare size={14} />
        </button>
        {hasContacts && (
          <button
            onClick={onSendEmail}
            className="p-0.5 text-slate-500 hover:text-blue-600 rounded"
            title="Enviar email"
          >
            <Mail size={14} />
          </button>
        )}
        {onDiscard && oferta?.status !== DEAL_STAGES.DESCARTADA && (
          <button
            onClick={onDiscard}
            className="p-0.5 text-slate-400 hover:text-red-600 rounded"
            title="Descartar oferta"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>

    {/* Frente B cascata_por_oferta 2026-04-26: cascata area→imoveis→propostas inline */}
    {cascataExpanded && hasArea && (
      <OfertaInlineCascata
        ofertaId={oferta.id}
        areaId={oferta.area_id}
        areaNome={oferta.areas?.nome}
        areaM2={oferta.areas?.area_total_m2}
        empresaId={oferta.empresa_id}
      />
    )}

    {/* Frente A resumo_enrich 2026-04-26: editor full-width abaixo da row */}
    {editingResumo && (
      <div className="mt-1 mb-2 border border-blue-300 rounded-lg p-3 bg-blue-50/40">
        <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wider mb-1.5 flex items-center gap-1">
          <Edit2 size={10} /> Editando resumo do estagio
          <span className="text-slate-500 normal-case tracking-normal ml-1">— {oferta?.nome || 'oferta'}</span>
        </div>
        <textarea
          value={editResumoText}
          onChange={(e) => setEditResumoText(e.target.value)}
          disabled={savingResumo}
          autoFocus
          className="w-full border border-slate-300 rounded p-2 text-sm min-h-[120px] focus:ring-2 focus:ring-blue-400 outline-none resize-y"
          placeholder="Descreva o estado atual do estagio..."
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSaveResumo();
            if (e.key === 'Escape') handleCancelEditResumo();
          }}
        />
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[10px] text-slate-500">
            Ctrl+Enter salva · Esc cancela
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleCancelEditResumo}
              disabled={savingResumo}
              className="px-3 py-1 text-xs text-slate-500 hover:bg-slate-100 rounded"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveResumo}
              disabled={savingResumo}
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
            >
              {savingResumo ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};
